import { useCallback, useState } from 'react';
import { Box, CircularProgress, Typography, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import PhotoIcon from '@mui/icons-material/Photo';
import { useDropzone } from 'react-dropzone';
import { Button } from '@baseComponents';
import { createFile } from '../../../networkRequest/filesService';
import {
  removeOfficialIdCard,
  updateOfficialIdCards,
  IdCardSide,
  OfficialData,
} from '../../../networkRequest/officialsService';
import { useUser } from '../../../hooks/useUser';
import Confirmation from '../../../components/confirmation/Confirmation';

// Caps how tall an ID photo can get; landscape photos are bounded by the card width
const MAX_IMAGE_HEIGHT = 196;

// Both sides are the same height so an empty one doesn't collapse next to a populated one
const CARD_HEIGHT = 296;

const ACCEPTED_FILE_TYPES = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
};

// What a card is currently doing; doubles as the spinner's accessible name
type CardAction = 'uploading' | 'removing';

const ID_SIDES = {
  front: { label: 'ID Front', fileIdField: 'front_file_id' },
  back: { label: 'ID Back', fileIdField: 'back_file_id' },
} as const;

const baseCardSx = {
  flex: '1 1 300px',
  maxWidth: 480,
  minHeight: CARD_HEIGHT,
  borderRadius: '10px',
  display: 'flex',
  flexDirection: 'column',
} as const;

// Text buttons sit tighter than the default outlined/contained ones
const textButtonSx = { borderRadius: '4px', paddingLeft: 1, paddingRight: 1 };

interface LawEnforcementIdSectionProps {
  officialId: number;
  frontUrl?: string | null;
  backUrl?: string | null;
  setData: (official: OfficialData) => void;
  refreshData: () => Promise<void>;
  setError: (message: string) => void;
}

interface IdCardProps {
  label: string;
  imageUrl?: string | null;
  busyAction?: CardAction;
  onFileSelected: (file: File) => void;
  onRemove: () => void;
}

const IdCard = ({ label, imageUrl, busyAction, onFileSelected, onRemove }: IdCardProps) => {
  const theme = useTheme();

  const onDropAccepted = useCallback(
    (files: File[]) => onFileSelected(files[0]),
    [onFileSelected]
  );

  // A populated card opens the picker from its Replace button rather than from a click anywhere
  // on the card, so that clicking Remove doesn't also start an upload
  const hasImage = Boolean(imageUrl);
  const { getRootProps, getInputProps, open } = useDropzone({
    onDropAccepted,
    accept: ACCEPTED_FILE_TYPES,
    maxFiles: 1,
    noClick: hasImage,
    noDrag: hasImage,
    noKeyboard: hasImage,
  });

  const fileInput = (
    <input {...getInputProps()} aria-label={`upload a photo of the ${label.toLowerCase()}`} />
  );

  // Only a card with nothing to show is given over to the spinner; a card being replaced or
  // removed keeps its current image underneath so a failed request leaves the original visible
  if (busyAction && !imageUrl) {
    return (
      <Box
        sx={{
          ...baseCardSx,
          alignItems: 'center',
          justifyContent: 'center',
          border: `1px solid ${theme.palette.divider}`,
          px: 4,
          py: 3,
        }}
      >
        <CircularProgress aria-label={`${busyAction} the ${label.toLowerCase()}`} />
      </Box>
    );
  }

  if (!imageUrl) {
    return (
      <Box
        {...getRootProps()}
        sx={{
          ...baseCardSx,
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          padding: 3,
          border: `1px dashed ${theme.palette.blueGrey[500]}`,
          backgroundColor: theme.palette.blueGrey[50],
          cursor: 'pointer',
        }}
      >
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            backgroundColor: theme.palette.background.default,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <PhotoIcon sx={{ fontSize: 35, color: theme.palette.action.active }} />
        </Box>
        <Box sx={{
          textAlign: 'center'
        }}>
          <Typography variant='m' component='p'>{label}</Typography>
          <Typography variant='bodyMedium' component='p' sx={{
            mt: 0.5
          }}>
            Drop here or click to browse
          </Typography>
        </Box>
        {/* No onClick: the click bubbles to the dropzone root, which owns opening the file picker */}
        <Button
          mode='outlined'
          text={`Upload ${label}`}
          startIcon={<FileUploadIcon fontSize='small' sx={{ marginRight: 1 }} />}
        />
        {fileInput}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...baseCardSx,
        gap: 1,
        border: `1px solid ${theme.palette.divider}`,
        px: 4,
        py: 3,
      }}
    >
      <Typography variant='s' component='p'>{label}</Typography>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <Box
          component='img'
          src={imageUrl}
          alt={`${label} of the user's law enforcement ID card`}
          sx={{
            display: 'block',
            // Sized from the image's own aspect ratio so the framed area hugs it
            // regardless of whether the ID was photographed landscape or portrait
            width: 'auto',
            height: 'auto',
            maxWidth: '100%',
            maxHeight: MAX_IMAGE_HEIGHT,
            borderRadius: '10px',
            boxShadow: theme.shadows[4],
            opacity: busyAction ? 0.3 : 1,
          }}
        />
        {busyAction && (
          <CircularProgress
            aria-label={`${busyAction} the ${label.toLowerCase()}`}
            sx={{ position: 'absolute' }}
          />
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Button
          mode='text'
          text='Remove'
          onClick={onRemove}
          disabled={Boolean(busyAction)}
          startIcon={<CloseIcon fontSize='small' sx={{ marginRight: 1 }} />}
          sx={{ ...textButtonSx, ...(busyAction ? {} : { color: theme.palette.error.main }) }}
        />
        <Button
          mode='text'
          text='Replace'
          onClick={open}
          disabled={Boolean(busyAction)}
          startIcon={<FileUploadIcon fontSize='small' sx={{ marginRight: 1 }} />}
          sx={textButtonSx}
        />
      </Box>
      {fileInput}
    </Box>
  );
};

const LawEnforcementIdSection = ({
  officialId,
  frontUrl,
  backUrl,
  setData,
  refreshData,
  setError,
}: LawEnforcementIdSectionProps) => {
  const { user } = useUser();
  // A card can only be doing one thing at a time, so a single record covers both sides
  const [busy, setBusy] = useState<{ side: IdCardSide; action: CardAction } | null>(null);
  const [sidePendingRemoval, setSidePendingRemoval] = useState<IdCardSide | null>(null);

  const busyActionFor = (side: IdCardSide) => (busy?.side === side ? busy.action : undefined);

  const handleFileSelected = async (side: IdCardSide, file: File) => {
    if (!user) return;

    setBusy({ side, action: 'uploading' });
    const { data: storedFile, error: uploadError } = await createFile(user.accessToken, file);

    if (storedFile) {
      // Persist the new file against the official right away so the upload isn't left dangling
      const { data, error } = await updateOfficialIdCards(user.accessToken, officialId, {
        [ID_SIDES[side].fileIdField]: storedFile.file_id,
      });

      if (data) {
        setData(data);
      } else {
        setError(error ?? `Failed to save the ${ID_SIDES[side].label}.`);
      }
    } else {
      setError(uploadError ?? `Failed to upload the ${ID_SIDES[side].label}.`);
    }

    setBusy(null);
  };

  const handleConfirmRemove = async () => {
    const side = sidePendingRemoval;
    if (!user || !side) return;

    setSidePendingRemoval(null);
    setBusy({ side, action: 'removing' });

    // The endpoint responds 204 with no body, so success is the absence of an error rather than
    // the presence of data, and the official has to be refetched to pick up the cleared image
    const { error } = await removeOfficialIdCard(user.accessToken, officialId, side);

    if (error) {
      setError(error);
    } else {
      await refreshData();
    }

    setBusy(null);
  };

  return (
    <Box>
      <Typography variant='l' component='h2'>Law Enforcement ID</Typography>
      <Typography variant='bodyMedium' component='p' sx={{
        mt: 0.5
      }}>
        Upload a clear photo of the front and back of the officer&apos;s law-enforcement ID card.
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, mt: 2 }}>
        <IdCard
          label={ID_SIDES.front.label}
          imageUrl={frontUrl}
          busyAction={busyActionFor('front')}
          onFileSelected={(file) => handleFileSelected('front', file)}
          onRemove={() => setSidePendingRemoval('front')}
        />
        <IdCard
          label={ID_SIDES.back.label}
          imageUrl={backUrl}
          busyAction={busyActionFor('back')}
          onFileSelected={(file) => handleFileSelected('back', file)}
          onRemove={() => setSidePendingRemoval('back')}
        />
      </Box>
      <Confirmation
        open={Boolean(sidePendingRemoval)}
        message={`Removing the ${sidePendingRemoval ? ID_SIDES[sidePendingRemoval].label : ''} image will permanently delete it from this user's Law Enforcement ID. Are you sure?`}
        onConfirm={handleConfirmRemove}
        onCancel={() => setSidePendingRemoval(null)}
        confirmText='Remove'
        cancelText='Cancel'
        accessibleTitle='Remove Law Enforcement ID Image Confirmation'
      />
    </Box>
  );
};

export default LawEnforcementIdSection;
